import { useState } from "react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { DIMENSIONS, MATRIX_KEYS, LADDER, BANDS } from "../lib/bopta";
import { ScenarioCard, ScenarioMatrix } from "../components/Scenarios";

const ViewToggle = ({ manager, setManager }) => (
  <div className="inline-flex rounded-full border border-[var(--line)] bg-white p-1 text-sm" data-testid="framework-view-toggle">
    {[[false, "Staff view"], [true, "Manager view"]].map(([v, l]) => (
      <button key={l} type="button" onClick={() => setManager(v)} className={`rounded-full px-4 py-1.5 transition-colors ${manager === v ? "bg-[var(--ink)] text-white" : ""}`} data-testid={`framework-view-${v ? "manager" : "staff"}`}>{l}</button>
    ))}
  </div>
);

const FrameworkScenarios = () => {
  const [manager, setManager] = useState(false);
  const [active, setActive] = useState(null);
  const pick = (k) => {
    setActive(k);
    document.getElementById(`scenario-${k}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section id="matrix" className="mt-20" data-testid="framework-matrix">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="kicker">Role-to-autonomy matrix</p><h2 className="mt-3 text-3xl font-bold">Every result lands in one of nine scenarios.</h2><p className="mt-2 text-sm muted">Role tier × overall autonomy, where overall = (B + O + P + T + A) ÷ 5. Managers are placed by their direct reports' role and the score they gave their team.</p></div>
        <ViewToggle manager={manager} setManager={setManager} />
      </div>
      <div className="panel mt-8 max-w-3xl p-6"><ScenarioMatrix active={active} onSelect={pick} manager={manager} /></div>
      <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {MATRIX_KEYS.map((k) => <ScenarioCard key={k} k={k} manager={manager} id={`scenario-${k}`} testid={`framework-scenario-${k}`} />)}
      </div>
      <p className="kicker mt-10">Reporting-line overlay</p>
      <p className="mt-2 max-w-3xl text-sm muted">Applies on top of the matrix scenario for staff results: Senior or Junior Staff reporting to a Team Lead or HOD, with overall autonomy below 50.</p>
      <div className="mt-4 max-w-xl"><ScenarioCard k="E" manager={manager} id="scenario-E" testid="framework-scenario-E" /></div>
      <p className="mt-4 text-sm muted">Discussion prompts, not diagnoses.</p>
    </section>
  );
};

const THEORIES = [
  ["Situational Leadership", "Hersey & Blanchard", "Leadership adapts to team competence. Low scores often mean you're stuck in 'Directing'. Prove competency and reliability to move your manager to 'Delegating'."],
  ["Technician vs. System Builder", "Michael Gerber · The E-Myth", "Waiting for your manager to solve problems is technician work. Autonomy comes from building the rules the technology and junior staff follow."],
  ["The Pareto Principle", "The 80/20 rule", "Master the critical 20% of high-impact tasks (revenue, fulfillment) to earn the trust to act independently on the rest."],
];

const DimensionPanel = ({ d }) => (
  <TabsContent value={d.k} className="mt-8 space-y-8" data-testid={`framework-panel-${d.k}`}>
    <p className="text-xl font-semibold">{d.lead}</p>
    {d.questions.map((q, i) => (
      <div key={q.q}>
        <p className="mb-3 font-medium"><span className="mr-2 muted">{d.k}{i + 1}</span>{q.q}</p>
        <div className="grid gap-2 md:grid-cols-5">
          {q.anchors.map((a) => (
            <div key={a.v} className="panel p-4">
              <p className="text-xs font-bold" style={{ color: d.color }}>{a.v} · {a.t}</p>
              <p className="mt-2 text-xs muted">{a.d}</p>
            </div>
          ))}
        </div>
      </div>
    ))}
  </TabsContent>
);

export default function Framework() {
  return (
    <div className="wrap py-16">
      <p className="kicker">The framework</p>
      <h1 className="mt-3 text-4xl font-extrabold sm:text-5xl">Measure dependency, not effort.</h1>
      <p className="mt-4 max-w-2xl muted">Not "How busy am I?" but "What moves without permission?" Each dimension asks how much your daily work relies on your direct manager. Higher = more independence. Required approvals and sensible escalation can be appropriate.</p>

      <Tabs defaultValue="B" className="mt-12">
        <TabsList className="h-auto flex-wrap gap-2 bg-transparent p-0">
          {DIMENSIONS.map((d) => (
            <TabsTrigger key={d.k} value={d.k} className="rounded-lg border border-[var(--line)] bg-white px-4 py-2 data-[state=active]:bg-[var(--ink)] data-[state=active]:text-white" data-testid={`framework-tab-${d.k}`}>
              {d.k} · {d.name}
            </TabsTrigger>
          ))}
        </TabsList>
        {DIMENSIONS.map((d) => <DimensionPanel key={d.k} d={d} />)}
      </Tabs>

      <FrameworkScenarios />

      <section className="mt-20">
        <p className="kicker">Six levels of delegation · shared with VentureBaseline</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {LADDER.map((l) => <div key={l.n} className="panel p-4" data-testid={`framework-level-${l.n}`}><p className="display text-3xl font-extrabold text-[var(--teal)]">L{l.n}</p><p className="mt-1 text-[11px] font-bold uppercase tracking-wider muted">{l.translation} · {l.range}</p><p className="mt-2 text-sm font-semibold">{l.growth}</p><p className="mt-1 text-xs muted">{l.reality}</p><p className="mt-2 text-xs"><span className="muted">Manager style:</span> {l.persona}</p></div>)}
        </div>
      </section>

      <section id="bandwidth" className="mt-20" data-testid="framework-bandwidth">
        <p className="kicker">Mental bandwidth</p>
        <h2 className="mt-3 text-3xl font-bold">Autonomy is what you're allowed. Bandwidth is what you can carry.</h2>
        <p className="mt-3 max-w-3xl muted">Each dimension adds one planning-horizon question: how far ahead your work runs before anyone checks in.</p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(BANDS).map(([b, d], i) => <div key={b} className="panel p-5"><p className="text-xs font-bold text-[var(--orange)]">0{i + 1}</p><p className="mt-1 text-lg font-bold">{b}</p><p className="mt-2 text-sm muted">{d}</p></div>)}
        </div>
        <p className="mt-4 text-sm muted">Results compare bandwidth with autonomy: <b>untapped capacity</b>, <b>stretched</b> or <b>aligned</b>. Grounded in Elliott Jaques' Stratified Systems Theory.</p>
      </section>

      <section className="mt-20 grid gap-4 md:grid-cols-3">
        {THEORIES.map(([t, a, d]) => <div key={t} className="panel p-6"><p className="text-xs muted">{a}</p><p className="mt-2 text-lg font-bold">{t}</p><p className="mt-2 text-sm muted">{d}</p></div>)}
      </section>
      <Link to="/assessment" className="btn btn-main mt-12" data-testid="framework-start">Apply the framework</Link>
    </div>
  );
}
